import { useState } from 'react'

import { produce } from 'immer'
import { useQueryClient } from 'react-query'
import styled from 'styled-components'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { IconThreeDots } from 'assets/svg/sprite'
import { PostInfoBottom } from 'components-ref/molecules'
import { useLikePost, useMediaCounters } from 'helpers/hooks'
import { orderMediaFiles } from 'helpers/media'
import { renderPostText } from 'helpers/postHelpers'
import colors from 'styles/colors'
import { type IPost } from 'types/interfaces/ITypes'

import { MediaItemSidebar } from '../MediaItemSidebar'
import { MediaItemAudio } from './components/MediaItemAudio'
import { MediaItemLive } from './components/MediaItemLive'
import { MediaItemProfile } from './components/MediaItemProfile'
import { MediaItemVideo } from './components/MediaItemVideo'
import styles from './mediaItem.module.scss'

interface Props {
    postData: IPost
    isOpen?: boolean
    setIsOpen?: (_value: boolean) => void
    setIsActive: (id: string) => void
    hideOptions?: boolean
    wrapperWidth?: string
    isMessage?: boolean
    hideLikeIfDeleted?: boolean
    hideSidebar?: boolean
    onVideoPlaybackChange?: (state: {
        videoId: string
        time: number
        duration: number
        playing: boolean
    }) => void
}

export const MediaItem = ({
    postData,
    isOpen,
    setIsOpen,
    setIsActive,
    hideOptions = false,
    wrapperWidth = '28.5rem',
    isMessage,
    hideLikeIfDeleted = false,
    hideSidebar = false,
    onVideoPlaybackChange
}: Props) => {
    const queryClient = useQueryClient()
    const { counter } = useMediaCounters(postData)
    const [inlineVideoPlaying, setInlineVideoPlaying] = useState(false)

    const hideLikeForPost =
        (hideLikeIfDeleted && !!(postData as any)?.deleted) ||
        postData?.type?.toLowerCase?.() === 'message'

    const { media, previews } = orderMediaFiles(postData)

    const onLikeMutate = async () => {
        const previousData = queryClient.getQueryData<{
            like: IPost[]
            purchases: IPost[]
            watched: IPost[]
        }>(['getMediaData'])

        const newData = produce(previousData, draft => {
            const toggleLike = (post?: IPost) => {
                if (!post) return

                post.isLiked = !post.isLiked
                post.likes_count += post.isLiked ? 1 : -1
            }

            toggleLike(draft?.like.find(post => post.id === postData.id))
            toggleLike(draft?.purchases.find(post => post.id === postData.id))
            toggleLike(draft?.watched.find(post => post.id === postData.id))
        })

        queryClient.setQueryData(['getMediaData'], newData)

        return { previousData }
    }

    const onLikeError = (err: any, context: any) => {
        console.error(err)
        queryClient.setQueryData(['getMediaData'], context.previousData)
    }

    const onLikeSuccess = () => {
        queryClient.invalidateQueries('getMediaData')
    }

    const { likePost } = useLikePost(
        postData.id,
        postData.isLiked,
        onLikeMutate,
        onLikeError,
        undefined,
        onLikeSuccess
    )

    const setActivePost = () => {
        if (postData?.id) {
            setIsActive(postData.id)
        }
    }

    const toggleOptions = (event: React.MouseEvent) => {
        event.stopPropagation()
        setActivePost()
        setIsOpen?.(!isOpen)
    }

    const renderSidebar = (hideLike = false, hideWhileVideoPlaying = false) => {
        if (
            hideSidebar ||
            hideLikeForPost ||
            (hideWhileVideoPlaying && inlineVideoPlaying)
        ) {
            return null
        }

        return (
            <div className={styles.sidebar}>
                <MediaItemSidebar
                    likeCb={hideLike ? undefined : () => likePost.mutate()}
                    liked={postData.isLiked}
                    count={postData.likes_count}
                    price={postData.price}
                    isMessage={isMessage}
                    hideLike={hideLike as any}
                />
            </div>
        )
    }

    const renderOptions = () => {
        if (hideOptions) return null

        return (
            <div className={`${styles.options} media__options`} onClick={toggleOptions}>
                <IconThreeDots color={colors.white} />
            </div>
        )
    }

    const renderPostInfo = () => (
        <PostInfoBottom
            postId={postData.id}
            role='owner'
            price={null}
            mediaCount={counter}
            hideCounter={false}
            countOnly={false}
        />
    )

    const renderProfile = () => (
        <MediaItemProfile
            username={postData?.user?.username}
            avatarUrl={postData?.user?.avatar?.url ?? placeholderAvatar}
            display_name={postData?.user?.display_name ?? postData?.user?.name}
            createdAt={postData?.created_at}
        />
    )

    if (postData.goal?.background_src) {
        return (
            <Wrapper $wrapperWidth={wrapperWidth} onClick={setActivePost}>
                <MediaWrapper>
                    {renderSidebar()}
                    {renderOptions()}

                    <div
                        className={styles.media}
                        style={{
                            backgroundColor: (postData.goal as any)?.background_color ?? undefined
                        }}
                    >
                        <img src={postData.goal.background_src} alt='goal background' />
                        {renderPostInfo()}
                    </div>
                </MediaWrapper>

                {renderProfile()}
            </Wrapper>
        )
    }

if (
    postData.poll &&
    !Array.isArray(postData.poll)
) {
    const bgSrc =
        postData.poll?.background_src_thumb ??
        postData.poll?.background_src ??
        undefined

    const bgColor =
        postData.poll?.background_color ??
        undefined

    return (
        <Wrapper
            $wrapperWidth={wrapperWidth}
            onClick={setActivePost}
            className={styles.wrapper}
        >
            <MediaWrapper>
                {renderSidebar()}
                {renderOptions()}

                <div
                    className={styles.media}
                    style={{
                        backgroundColor: bgColor
                    }}
                >
                    {bgSrc && (
                        <img
                            src={bgSrc}
                            alt='poll background'
                        />
                    )}

                    {renderPostInfo()}
                </div>
            </MediaWrapper>

            {renderProfile()}
        </Wrapper>
    )
}

    if (postData.live) {
        return <MediaItemLive live={postData.live} />
    }

    if (postData.price && previews.length > 0) {
        const data = previews[0]
        const hideLike = hideLikeForPost || !!(data as any)?.deleted
        const isVideo = data.preview.type === 'clip'

        return (
            <Wrapper $wrapperWidth={wrapperWidth} onClick={setActivePost} className={styles.wrapper}>
                <MediaWrapper>
                    {renderSidebar(hideLike, isVideo)}
                    {renderOptions()}

                    <div className={styles.media}>
                        {isVideo && (
                            <MediaItemVideo
                                url={data.preview.src}
                                thumbnail={data.preview.active_thumb}
                                cloudfront={
                                    'cloudfront' in data.preview
                                        ? data.preview.cloudfront
                                        : null
                                }
                                onPlaybackChange={state => {
                                  onVideoPlaybackChange?.({
                                      videoId: String(data.id),
                                      ...state
                                  })
                              }}
                                
                            />
                        )}

                        {(data.preview.type === 'thumb' || data.preview.type === 'blur') && (
                            <img src={data.preview.src} alt='media' />
                        )}

                        {renderPostInfo()}
                    </div>
                </MediaWrapper>

                {renderProfile()}
            </Wrapper>
        )
    }

    if (media.length > 0) {
        const data = media[0]
        const hideLike = hideLikeForPost || !!(data as any)?.deleted
        const isVideo = data.type === 'video'

        return (
            <Wrapper
                    $wrapperWidth={wrapperWidth}
                    onClick={setActivePost}
                    className={styles.wrapper}
                >
                <MediaWrapper>
                    {renderSidebar(hideLike, isVideo)}
                    {renderOptions()}

                    <div className={styles.media}>
                        {data.type === 'video' && (
                            <MediaItemVideo
                                url={data.url}
                                thumbnail={data.active_thumb}
                                cloudfront={data.cloudfront}
                                onPlaybackChange={state => {
                                    onVideoPlaybackChange?.({
                                        videoId: String(data.id),
                                        ...state
                                    })
                                }}
                            />
                        )}

                        {data.type === 'photo' && (
                            <img src={data.url} alt='media' />
                        )}

                        {(data.type === 'audio' || data.type === 'sound') && (
                            <MediaItemAudio url={data.url} />
                        )}

                        {renderPostInfo()}
                    </div>
                </MediaWrapper>

                {renderProfile()}
            </Wrapper>
        )
    }

    return (
        <Wrapper $wrapperWidth={wrapperWidth} onClick={setActivePost}>
            <MediaWrapper className={styles.text}>
                {renderSidebar()}
                {renderOptions()}

                <div className={styles.media}>
                    <p>{renderPostText(postData.body ?? '', postData.tags)}</p>
                    {renderPostInfo()}
                </div>
            </MediaWrapper>

            {renderProfile()}
        </Wrapper>
    )
}

interface WrapperProps {
    $wrapperWidth?: string
}

const Wrapper = styled.div<WrapperProps>`
    width: ${props => props.$wrapperWidth};
    max-width: 28.5rem;
    min-width: 22rem;
`

const MediaWrapper = styled.div`
    position: relative;
`